import React from 'react';
import Formsy from 'formsy-react';
import _ from 'lodash';
import { Typography, Button, Grid, CircularProgress } from '@mui/material';
import CartSummary from 'components/CartSummary';
import RadioInput from 'components/inputs/RadioInput';
import TextInput from 'components/inputs/TextInput';
import CheckboxInput from 'components/inputs/CheckboxInput';
import { Link } from 'react-router-dom';
import { deliveryFee } from 'utils/fees';
import { getCountry } from 'utils/countries';
import renderHTML from 'html-react-parser';
import Warning from '@mui/icons-material/Warning';
import { FormattedMessage, injectIntl } from 'react-intl';

export class ConfirmStep extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      termsAccepted: '0',
      code: '',
      codeValue: 0
    };

    this.handleStoredCardSubmit = this.handleStoredCardSubmit.bind(this);
    this.formRef = React.createRef();
  }

  componentDidMount() {
    const { config, formData, handleDeliverySelect } = this.props;

    if (!formData.delivery_option_id) {
      handleDeliverySelect(config.delivery_options[0].id.toString());
    }
  }

  renderSummary(prefix) {
    const { formData } = this.props;

    return (
      <React.Fragment>
        <Typography variant='subtitle1'>{`${formData[`${prefix}_first_name`]} ${formData[`${prefix}_last_name`]}`}</Typography>
        { formData[`${prefix}_company_name`] &&
          <Typography variant='subtitle1'>{formData[`${prefix}_company_name`]}</Typography>
        }
        <Typography variant='subtitle1'>{formData[`${prefix}_street_address`]}</Typography>
        <Typography variant='subtitle1'>{`${formData[`${prefix}_suburb`]} ${formData[`${prefix}_state`]} ${formData[`${prefix}_postcode`]}`}</Typography>
        {formData[`${prefix}_country`] &&
          <Typography variant='subtitle1'>{getCountry(formData[`${prefix}_country`])['name']}</Typography>
        }
      </React.Fragment>
    );
  }

  handleStoredCardSubmit(data) {
    const { validateStoredCard, handleStoredCardSubmit } = this.props;

    validateStoredCard(data, handleStoredCardSubmit, this.formRef);
  }

  termsLink() {
    const customPages = _.flatten(_.map(this.props.site.custom_page_groups, 'custom_pages'));
    const termsPage = _.find(customPages, {template: 'terms_and_conditions'});

    if (termsPage?.content_external) {
      return termsPage.content_url;
    }
    return window.BASE_PATH + (termsPage ? `/${termsPage.path}` : '/terms_and_conditions');
  }

  render() {
    const { handleBack, handleNext, handleDeliverySelect, formData, site, cart, classes, config, digitalOnly, intl } = this.props;
    const requiredValidation= { isDefaultRequiredValue: intl.formatMessage({id: "validation_required", defaultMessage: "can't be blank"})};

    if (!formData.delivery_option_id) {
      return <CircularProgress />;
    }

    return (
      <React.Fragment>
        <CartSummary cart={cart} site={site} titleId='cart.confirmation'
          deliveryFee={digitalOnly ? 0 : deliveryFee(config.delivery_options, formData.delivery_option_id)} codeValue={this.state.codeValue}
          storedCard={{number: formData.stored_card_number, balance: formData.stored_card_amount}}
        />
        {site.closed_loop_redemptions && !site.closed_loop_checkout_step &&
          <React.Fragment>
            <Typography align='center' variant="h4">
              <FormattedMessage id="checkout.confirm.use_stored_card" defaultMessage="Have a {name} gift card?" values={{name: site.closed_loop_name}}/>
            </Typography>

            <Formsy onValidSubmit={this.handleStoredCardSubmit} className="full-width" ref={this.formRef}>
              <Grid container spacing={1} justifyContent='center' style={{marginBottom: '3em'}}>
                <Grid size={{xs: 12, md: 6}}>
                  <TextInput name="stored_card_number"
                             label={site.closed_loop_name + ' ' + intl.formatMessage({id: "checkout.confirm.stored_card_number", defaultMessage: "Card Number"})}
                             required
                    validationErrors={requiredValidation}
                  />
                </Grid>
                <Grid size={{xs: 12, md: 4}}>
                  <TextInput name="pin"
                             label={intl.formatMessage({id: "checkout.confirm.stored_card_pin", defaultMessage: "PIN"})}
                             required
                    validations="isLength:4"
                    validationErrors={_.merge(requiredValidation, {
                      isLength: intl.formatMessage({id: "checkout.confirm.stored_card_pin_invalid", defaultMessage: "must be 4 digits"})
                    })}
                  />
                </Grid>
                <Grid size={{xs: 4, md: 2}} align="center" style={{paddingTop: '36px'}}>
                  <Button type='submit' variant='contained'>
                    <FormattedMessage id="checkout.confirm.stored_card_submit" defaultMessage="Submit"/>
                  </Button>
                </Grid>
              </Grid>
            </Formsy>
          </React.Fragment>
        }
        <Formsy onValidSubmit={handleNext} className='full-width' id="confirm_step">
          <Grid container spacing={1} justifyContent='center'>
            <Grid size={{xs: 12, md: 10}}>
              <div style={{marginTop: '20px'}}>
                {!digitalOnly &&
                  <RadioInput name='delivery_option_id'
                              label={intl.formatMessage({id: "checkout.confirm.delivery", defaultMessage: "Delivery"})}
                              containerClass='top-space'
                    value={formData.delivery_option_id}
                    onChange={(value) => handleDeliverySelect(value)}
                    options={config.delivery_options.map((option) => {return {value: option.id.toString(), label: `${option.name} $${option.amount}`}})}
                  />
                }
              </div>

              <Typography align="center">
                <FormattedMessage id="checkout.confirm.receipt_email" defaultMessage="Invoice and tracking information to be sent to:"/>
                <span className="bold"> {formData.email}</span>
              </Typography>
              {!window.RISKIFIED_SESSION_ID &&
                <Typography align='center'>
                  <Warning style={{marginTop: '5px', marginBottom: '-5px'}} />
                  <FormattedMessage id="checkout.confirm.risk_warning" defaultMessage="You may be contacted to verify your identification before your cards are dispatched"/>
                </Typography>
              }
            </Grid>
            {site.checkout_confirmation_text &&
              <Grid size={{xs: 12, md: 10}}>
                <Typography component="span" color="textPrimary" align="center" className="top-space no-paragraph-space">
                  {renderHTML(site.checkout_confirmation_text)}
                </Typography>
              </Grid>
            }
          </Grid>
          <div style={{marginTop: '20px'}}>
            <Grid container spacing={1} justifyContent='center'>
              {!digitalOnly &&
                <Grid size={{xs: 12, md: 5}}>
                  <Typography variant='subtitle1' className="confirm-title">
                    <FormattedMessage id="checkout.confirm.deliver_to" defaultMessage="DELIVER TO"/>
                  </Typography>
                  {this.renderSummary('recipient')}
                </Grid>
              }
              <Grid size={{xs: 12, md: 5}}>
                <Typography variant='subtitle1' className="confirm-title">
                  <FormattedMessage id="checkout.confirm.billed_to" defaultMessage="BILLING TO"/>
                </Typography>
                { formData.billing_same_address === '1' ?
                  this.renderSummary('recipient')
                  :
                  this.renderSummary('billing')
                }
              </Grid>
              <Grid size={{xs: 12, md: 10}}>
                <CheckboxInput name='acceptance' value='0'
                               onChange={(v) => this.setState({termsAccepted: v})}
                               label={<span>
                                  {intl.formatMessage({id: "checkout.confirm.agreement", defaultMessage: "I agree to the"}) + ' '}
                                  <a href={this.termsLink()} target="_blank">
                                    <FormattedMessage id="checkout.confirm.terms" defaultMessage="Terms & Conditions" />
                                  </a></span>}
                               validations="equals:&quot;1&quot;"
                               validationErrors={{
                                 equals: intl.formatMessage({id: "checkout.confirm.terms_consent_invalid", defaultMessage: "Must tick to continue"})
                               }}
                />
              </Grid>
            </Grid>
          </div>

          <div className='button-container'>
            <Button variant='contained' color="grey" onClick={handleBack}>
              <FormattedMessage id="checkout.confirm.back" defaultMessage="Back"/>
            </Button>
            <Button variant='contained' color='primary' onClick={handleNext} disabled={this.state.termsAccepted !== '1'}>
              <FormattedMessage id="checkout.confirm.proceed" defaultMessage="Next"/>
            </Button>
          </div>
        </Formsy>
      </React.Fragment>
    )
  }
}

export default injectIntl(ConfirmStep);
